import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Loader2, Copy, ExternalLink, Share2, Lock, Rocket, Video, Zap, Link2, Repeat, Mail, Send, Tag } from "lucide-react";
import LinkRotationTab from "@/components/LinkRotationTab";
import UtmLinksTab from "@/components/UtmLinksTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TABS = [
  { key: "funnel", label: "Funnel Link", icon: Rocket, color: "#00863A" },
  { key: "discover", label: "Discover Link", icon: Video, color: "#7C3AED" },
  { key: "sizzle", label: "Sizzle-to-Funnel", icon: Zap, color: "#4060FF" },
  { key: "invite", label: "CaryPact Invite", icon: Link2, color: "#D97706" },
];
// Super admin only — the rotation decides which link a login-page sign-up gets.
const ROTATION_TAB = { key: "rotation", label: "Link rotation", icon: Repeat, color: "#0EA5E9" };
// Marketing managers (and admins) build tagged campaign links here.
const UTM_TAB = { key: "utm", label: "UTM links", icon: Tag, color: "#0EA5E9" };

function LinkBox({ url, tid, accent }) {
  return (
    <div className="font-mono text-xs leading-relaxed break-all rounded-lg px-3 py-2.5 select-all"
      style={{ background: "#F8FAFC", border: `1px solid ${accent || "rgba(15,18,38,0.1)"}`, color: "#0F1226" }}
      data-testid={tid}>
      {url}
    </div>
  );
}

function LockedNote() {
  return (
    <div className="flex items-start gap-3 rounded-xl px-4 py-3.5" style={{ background: "rgba(15,18,38,0.04)", border: "1px dashed rgba(15,18,38,0.18)" }} data-testid="share-links-locked">
      <Lock size={16} className="shrink-0 mt-0.5" style={{ color: "#94A3B8" }} />
      <p className="text-xs leading-relaxed" style={{ color: "#5A607F" }}>
        <b>This link is locked.</b> Set your CaryPact invite link first (see the <b>CaryPact Invite</b> tab) — every referral must carry your personal invite so new sign-ups register under you on CaryPact.
      </p>
    </div>
  );
}

function ShareByEmail({ linkType, accent, testidPrefix }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [fromName, setFromName] = useState("");
  const [to, setTo] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  // Defaults to their own name; they can send under any name they like.
  useEffect(() => { setFromName((v) => v || (user?.name || "")); }, [user?.name]);

  const send = async () => {
    const recipients = to.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    if (recipients.length === 0) { toast.error("Add at least one email address"); return; }
    setSending(true);
    try {
      const r = await api.post("/share/send", {
        link_type: linkType,
        recipients,
        note: note.trim() || undefined,
        from_name: fromName.trim() || undefined,
        origin: window.location.origin,
      });
      const d = r.data || {};
      if (d.sent > 0) {
        toast.success(`Sent to ${d.sent} ${d.sent === 1 ? "person" : "people"}${d.failed ? ` · ${d.failed} couldn't be delivered` : ""}`);
        setTo(""); setNote(""); setOpen(false);
      } else {
        toast.error("Couldn't send — double-check the addresses and try again");
      }
      if (d.invalid?.length) toast.error(`Skipped invalid: ${d.invalid.join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`email-toggle-${testidPrefix}`}>
        <Mail size={12} className="mr-1" /> Email this link
      </Button>
    );
  }
  return (
    <div className="rounded-xl p-4 space-y-3" style={{ background: `${accent}0a`, border: `1px solid ${accent}33` }} data-testid={`email-box-${testidPrefix}`}>
      <div className="flex items-center gap-2">
        <Mail size={15} style={{ color: accent }} />
        <span className="text-sm font-semibold" style={{ color: "#0F1226" }}>Send this link by email</span>
      </div>
      <p className="text-[11px] text-ink-3 leading-relaxed">
        Goes out from <b>hello@botchaincp.com</b> under the name below — replies come straight back to your inbox. Add a recipient's name and they'll be greeted by it, and show up identified (not "anonymous") the moment they click.
      </p>
      <div>
        <label className="text-[11px] font-medium text-ink-2">From <span className="text-ink-3">(the name they'll see)</span></label>
        <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder="Your name" className="mt-1 text-sm" data-testid={`email-from-${testidPrefix}`} />
      </div>
      <div>
        <label className="text-[11px] font-medium text-ink-2">Recipients <span className="text-ink-3">(comma-separated — you can include names)</span></label>
        <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Maria Lopez <maria@email.com>, carlos@email.com" className="mt-1 text-sm" data-testid={`email-recipients-${testidPrefix}`} />
      </div>
      <div>
        <label className="text-[11px] font-medium text-ink-2">Personal note <span className="text-ink-3">(optional)</span></label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hey! Thought you'd find this interesting — take a look when you get a sec." rows={3} className="mt-1 text-sm" data-testid={`email-note-${testidPrefix}`} />
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={sending} data-testid={`email-cancel-${testidPrefix}`}>Cancel</Button>
        <Button size="sm" onClick={send} disabled={sending || !to.trim()} data-testid={`email-send-${testidPrefix}`}>
          {sending ? <Loader2 className="animate-spin" size={12} /> : <><Send size={12} className="mr-1" /> Send</>}
        </Button>
      </div>
    </div>
  );
}

function ShareTab({ title, blurb, audience, url, accent, tid, copyLabel, testidPrefix, linkType }) {
  const copy = async (v, label) => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select the text and use Ctrl+C"); }
  };
  return (
    <div className="space-y-3" data-testid={tid}>
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight">{title}</h3>
        <p className="text-xs text-ink-3 mt-1 leading-relaxed">{blurb}</p>
        {audience && (
          <p className="text-[11px] mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium"
            style={{ background: `${accent}14`, color: accent }} data-testid={`${testidPrefix}-audience`}>
            {audience}
          </p>
        )}
      </div>
      {url ? (
        <>
          <LinkBox url={url} tid={`${testidPrefix}-input`} accent={accent} />
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={() => copy(url, copyLabel)} data-testid={`copy-${testidPrefix}`}>
              <Copy size={12} className="mr-1" /> Copy
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(url, "_blank")} data-testid={`open-${testidPrefix}`}>
              <ExternalLink size={12} className="mr-1" /> Open
            </Button>
          </div>
          {linkType && <ShareByEmail linkType={linkType} accent={accent} testidPrefix={testidPrefix} />}
        </>
      ) : (
        <LockedNote />
      )}
    </div>
  );
}

export default function AdminShareLinks() {
  const { user } = useAuth();
  // The UTM builder is for marketing managers ONLY — the ticked checkbox in
  // Members (the API already counts the super admin as one). Team leaders carry
  // the admin role, so gating on the role would hand it to every one of them.
  const canUtm = !!user?.marketing_access;
  // A marketing manager who isn't an admin only gets the campaign-link builder.
  const marketingOnly = !!user && user.role !== "admin" && canUtm;
  const [tab, setTab] = useState(marketingOnly ? "utm" : "funnel");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [funnel, setFunnel] = useState("");
  const [invite, setInvite] = useState("");
  const [inviteSource, setInviteSource] = useState(null);
  const [inviteInput, setInviteInput] = useState("");
  const [inviteEdit, setInviteEdit] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/me/carypact-invite")
      .then((r) => {
        const slug = r.data.referral_slug || "";
        const origin = window.location.origin;
        // Code only — it already names the wallet. The suffix is added back just
        // for the few wallets that share a code, where it is the only difference.
        const sfx = r.data.share_suffix_needed && r.data.carypact_suffix ? `&s=${r.data.carypact_suffix}` : "";
        setFunnel(slug && r.data.share_ready ? `${origin}/join?ref=${slug}${sfx}` : "");
        setInvite(r.data.carypact_invite_link || "");
        setInviteSource(r.data.carypact_invite_source || null);
        setInviteInput(r.data.carypact_invite_link || "");
        setInviteEdit(!r.data.carypact_invite_link);
      })
      .catch(() => toast.error("Failed to load your links"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (marketingOnly) { setLoading(false); return; } load(); }, [marketingOnly]);  // eslint-disable-line react-hooks/exhaustive-deps

  const saveInvite = async () => {
    setSaving(true);
    try {
      await api.post("/me/carypact-invite", { link: inviteInput.trim() });
      toast.success("Your CaryPact invite link is saved — share links unlocked");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const copy = async (v, label) => {
    try { await navigator.clipboard.writeText(v); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed — select the text and use Ctrl+C"); }
  };

  const discover = funnel ? funnel.replace("/join?ref=", "/discover?ref=") : "";
  const sizzleToFunnel = funnel ? funnel.replace("/join?ref=", "/watch?ref=") : "";

  if (user && user.role !== "admin" && !user.marketing_access) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen pb-16">
      <Header />
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-10">
        <div className="mb-6 fade-up">
          <div className="pill mb-3"><span className="dot" />Users · Share links</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Share2 size={26} className="text-indigo-600" /> My share links
          </h1>
          <p className="text-ink-2 text-sm mt-2 max-w-2xl">
            {marketingOnly
              ? "Build a tagged link for each campaign, creator or placement — then see exactly who came through it."
              : "Hand these out to new prospects. Every sign-up through your links is tagged to you — your ref code is your CaryPact invite code."}
          </p>
        </div>

        <div className="glass-strong p-5 md:p-6" data-testid="share-links-panel">
          {/* Tab bar */}
          <div className="flex items-center gap-1 p-1 rounded-xl mb-5 flex-wrap" style={{ background: "rgba(15,18,38,0.05)" }} data-testid="share-links-tabbar">
            {(marketingOnly
              ? [UTM_TAB]
              : [...TABS,
                 ...(canUtm ? [UTM_TAB] : []),
                 ...(user?.is_super_admin ? [ROTATION_TAB] : [])]).map((t) => {
              const Icon = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`share-tab-${t.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${active ? "bg-white shadow-sm" : "text-ink-2 hover:bg-white/50"}`}
                  style={active ? { color: t.color } : undefined}
                >
                  <Icon size={13} /> {t.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" /></div>
          ) : (
            <div>
              {tab === "funnel" && (
                <ShareTab
                  title="Your funnel link"
                  blurb="The direct path in. Opens the CaryPact onboarding flow — create account, watch the video, set up their wallet — with every sign-up credited to you."
                  audience="Best for people who are already interested."
                  url={funnel} accent="#00863A" tid="share-content-funnel" testidPrefix="funnel-link" copyLabel="Funnel link" linkType="funnel"
                />
              )}
              {tab === "discover" && (
                <ShareTab
                  title="Your discover link"
                  blurb="The soft first touch. Opens a short sizzle video, then the full presentation, then the onboarding flow. You can see exactly how much of each video a lead watched in your reports."
                  audience="Best for cold prospects who need warming up first."
                  url={discover} accent="#7C3AED" tid="share-content-discover" testidPrefix="discover-link" copyLabel="Discover link" linkType="discover"
                />
              )}
              {tab === "sizzle" && (
                <ShareTab
                  title="Sizzle to funnel"
                  blurb="The middle option. A short sizzle video, then straight into onboarding — no lead-capture form in between."
                  audience="Best for warm prospects who just need to see the hook."
                  url={sizzleToFunnel} accent="#4060FF" tid="share-content-sizzle" testidPrefix="sizzle-to-funnel-link" copyLabel="Sizzle-to-funnel link" linkType="sizzle"
                />
              )}
              {tab === "utm" && canUtm && <UtmLinksTab />}
              {tab === "rotation" && user?.is_super_admin && <LinkRotationTab />}
              {tab === "invite" && (
                <div className="space-y-3" data-testid="share-content-invite">
                  <div>
                    <h3 className="font-display text-lg font-semibold tracking-tight flex items-center gap-2">
                      Your personal CaryPact invite link <span className="text-red-600">*</span>
                    </h3>
                    <p className="text-xs text-ink-3 mt-1 leading-relaxed">
                      Required before the other links work. It&apos;s where your recruits actually register on CaryPact, and its <code className="px-1 rounded bg-black/5">inviteCode</code> is what tags every other link back to you — kept in sync with your wallet where possible.
                    </p>
                  </div>
                  {!inviteEdit && invite ? (
                    <>
                      <LinkBox url={invite} tid="carypact-invite-input" accent="#D97706" />
                      {inviteSource === "synced" && (
                        <p className="text-[11px] text-emerald-700" data-testid="carypact-invite-auto-note">
                          Auto-filled live from your synced CaryPact wallet — always current.
                        </p>
                      )}
                      {inviteSource === "tile" && (
                        <p className="text-[11px] text-ink-3" data-testid="carypact-invite-tile-note">
                          Taken from your wallet&apos;s tile in the org chart. Change it below to override.
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => copy(invite, "CaryPact invite")} data-testid="copy-carypact-invite">
                          <Copy size={12} className="mr-1" /> Copy
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setInviteEdit(true)} data-testid="edit-carypact-invite">
                          Change
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {!invite && (
                        <p className="text-xs rounded-lg px-3 py-2" style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="carypact-invite-empty-hint">
                          No invite link found yet. Two ways to fill it: <b>sync your wallet</b> (one tap — it auto-fills this and stays current), or paste it from the CaryPact app: User Center → Invite Friends → Copy link.
                        </p>
                      )}
                      <Input
                        value={inviteInput}
                        onChange={(e) => setInviteInput(e.target.value)}
                        placeholder="https://app.carypact.com/?inviteCode=XXXX&suffix=YYYY"
                        className="font-mono text-xs"
                        data-testid="carypact-invite-editor"
                      />
                      <div className="flex gap-2 justify-end">
                        {invite && (
                          <Button size="sm" variant="ghost" onClick={() => { setInviteInput(invite); setInviteEdit(false); }}>
                            Cancel
                          </Button>
                        )}
                        <Button size="sm" onClick={saveInvite} disabled={saving || !inviteInput.trim()} data-testid="save-carypact-invite">
                          {saving ? <Loader2 className="animate-spin" size={12} /> : "Save"}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
